import {store} from "@core/store";
import get from "lodash/get";
import {ConfirmDialogLabel, FormLabel, TableLabel} from "@components";
import {
  amber,
  blue,
  blueGrey,
  brown,
  deepOrange,
  deepPurple,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  teal,
} from "@mui/material/colors";
import {Asset, AssetType, Role, User, WithPermissions} from "@core/model";
import React from "react";
import {
  InsertDriveFile,
  PictureAsPdf,
  Image,
  Movie,
  Audiotrack,
  Code,
  Description,
  Archive,
  TextSnippet,
} from "@mui/icons-material";
import {
  Color,
  darken,
  lighten,
  SvgIconOwnProps,
  SxProps,
  Theme,
} from "@mui/material";
import {colorScheme} from "@core/store/slices/appSlice";
import {AppTheme} from "@theme";
import {
  AssetBuildingIcon,
  AssetIcon,
  AssetMachineIcon,
  AssetProductIcon,
  AssetProductionIcon,
  AssetVehicleIcon,
  SensorMultivalueIcon,
  SensorScalarIcon,
} from "@components/icons";
import {ticketPriority} from "@core/model/Ticket";

export type CrudProps = {
  confirmDialogLabel: ConfirmDialogLabel;
  formLabel: FormLabel;
  tableLabel: Partial<TableLabel>;
};

const acronym = (text: string) => {
  const splitted = text.trim().split(" ");
  if (splitted.length > 1) {
    return (splitted[0][0] + "" + splitted[1][0]).toUpperCase();
  } else if (text.length > 1) {
    return (text[0] + "" + text[1]).toUpperCase();
  } else {
    return text.toUpperCase();
  }
};

// Mappa dei livelli per Light Mode
const lightModeLevels: Record<
  string,
  {lightness: number; darkenFactor: number}
> = {
  "50": {lightness: 0.85, darkenFactor: -0.06},
  "100": {lightness: 0.7, darkenFactor: -0.05},
  "200": {lightness: 0.5, darkenFactor: -0.04},
  "300": {lightness: 0.3, darkenFactor: -0.03},
  "400": {lightness: 0.2, darkenFactor: -0.02},
  "500": {lightness: 0, darkenFactor: 0},
  "600": {lightness: -0.1, darkenFactor: 0.05},
  "700": {lightness: -0.2, darkenFactor: 0.08},
  "800": {lightness: -0.3, darkenFactor: 0.1},
  "900": {lightness: -0.4, darkenFactor: 0.15},
  A100: {lightness: 0.8, darkenFactor: -0.08},
  A200: {lightness: 0.6, darkenFactor: -0.06},
  A400: {lightness: 0.4, darkenFactor: -0.04},
  A700: {lightness: 0.2, darkenFactor: -0.02},
};

// Mappa dei livelli per Dark Mode (più acida, satura e luminosa)
const darkModeLevels: Record<
  string,
  {lightness: number; darkenFactor: number}
> = {
  "50": {lightness: 0.75, darkenFactor: -0.1},
  "100": {lightness: 0.6, darkenFactor: -0.08},
  "200": {lightness: 0.4, darkenFactor: -0.06},
  "300": {lightness: 0.3, darkenFactor: -0.04},
  "400": {lightness: 0.15, darkenFactor: -0.03},
  "500": {lightness: 0, darkenFactor: 0},
  "600": {lightness: 0.1, darkenFactor: 0.1},
  "700": {lightness: 0.2, darkenFactor: 0.15},
  "800": {lightness: 0.3, darkenFactor: 0.2},
  "900": {lightness: 0.4, darkenFactor: 0.3},
  A100: {lightness: 0.7, darkenFactor: -0.06},
  A200: {lightness: 0.5, darkenFactor: -0.04},
  A400: {lightness: 0.3, darkenFactor: -0.02},
  A700: {lightness: 0.1, darkenFactor: -0.01},
};

// Funzione per generare una palette in base alla modalità
const generateColorPalette = (
  color: string,
  isDarkMode: boolean,
): Record<string, string> => {
  const levels = isDarkMode ? darkModeLevels : lightModeLevels;
  const palette: Record<string, string> = {};

  Object.keys(levels).forEach(level => {
    const {lightness} = levels[level];
    palette[level] =
      lightness >= 0 ? lighten(color, lightness) : darken(color, -lightness);
  });

  return palette;
};

const getColor = (
  index: number,
  mode: colorScheme,
  levels?: {dark: keyof Color; light: keyof Color},
): string => {
  const darkLevel = levels?.dark ?? 600;
  const lightLevel = levels?.light ?? 300;
  const colors = [
    amber[mode === "dark" ? darkLevel : lightLevel],
    blue[mode === "dark" ? darkLevel : lightLevel],
    blueGrey[mode === "dark" ? darkLevel : lightLevel],
    brown[mode === "dark" ? darkLevel : lightLevel],
    deepOrange[mode === "dark" ? darkLevel : lightLevel],
    deepPurple[mode === "dark" ? darkLevel : lightLevel],
    indigo[mode === "dark" ? darkLevel : lightLevel],
    lightBlue[mode === "dark" ? darkLevel : lightLevel],
    lightGreen[mode === "dark" ? darkLevel : lightLevel],
    lime[mode === "dark" ? darkLevel : lightLevel],
    orange[mode === "dark" ? darkLevel : lightLevel],
    pink[mode === "dark" ? darkLevel : lightLevel],
    purple[mode === "dark" ? darkLevel : lightLevel],
    teal[mode === "dark" ? darkLevel : lightLevel],
  ];

  const i = index < colors.length ? index : index % colors.length;

  return colors[i];
};

const getUserAvatarColor = (user: User, darkMode: boolean) => {
  const index = parseInt((user.id as string).substring(15, 24), 16);
  const color = getColor(index, darkMode ? "dark" : "light");
  return color;
};

const hasPermission = (
  user: User,
  entity: WithPermissions,
  operation: "EDIT" | "DELETE",
) => {
  if (user.role === "ADMIN" || user.role === "TENANT_ADMIN") {
    return true;
  }

  if (user.id === entity.userId) {
    return true;
  }

  return entity.permissions
    ?.map(p => {
      const hasOperation = p.operations.findIndex(o => o === operation) !== -1;
      if (!hasOperation) {
        return false;
      }

      if (p.type === "GROUP") {
        return user.groupIds?.findIndex(gid => gid === p.value) !== -1;
      } else if (p.type === "USER") {
        return p.value === user.id;
      }
      return false;
    })
    .some(check => check);
};

const iconMap: {[key: string]: React.ReactElement} = {
  // Documenti
  pdf: <PictureAsPdf color="error" />,
  doc: <Description color="primary" />,
  docx: <Description color="primary" />,
  txt: <TextSnippet color="secondary" />,
  rtf: <TextSnippet color="secondary" />,

  // Immagini
  jpg: <Image color="success" />,
  jpeg: <Image color="success" />,
  png: <Image color="success" />,
  gif: <Image color="success" />,
  bmp: <Image color="success" />,
  svg: <Image color="success" />,

  // Video
  mp4: <Movie color="action" />,
  avi: <Movie color="action" />,
  mkv: <Movie color="action" />,
  mov: <Movie color="action" />,

  // Audio
  mp3: <Audiotrack color="primary" />,
  wav: <Audiotrack color="primary" />,
  flac: <Audiotrack color="primary" />,
  ogg: <Audiotrack color="primary" />,

  // Archivi
  zip: <Archive color="warning" />,
  rar: <Archive color="warning" />,
  "7z": <Archive color="warning" />,
  tar: <Archive color="warning" />,
  gz: <Archive color="warning" />,

  // Codice
  js: <Code color="info" />,
  ts: <Code color="info" />,
  jsx: <Code color="info" />,
  tsx: <Code color="info" />,
  html: <Code color="info" />,
  css: <Code color="info" />,
  json: <Code color="info" />,
  xml: <Code color="info" />,
};

const getExtensionFromMimeType = (mimeType: string): string | null => {
  const mimeMap: {[key: string]: string} = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "text/plain": "txt",
    "application/rtf": "rtf",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/bmp": "bmp",
    "image/svg+xml": "svg",
    "video/mp4": "mp4",
    "video/x-msvideo": "avi",
    "video/x-matroska": "mkv",
    "audio/mpeg": "mp3",
    "audio/wav": "wav",
    "audio/ogg": "ogg",
    "application/zip": "zip",
    "application/x-rar-compressed": "rar",
    "application/x-7z-compressed": "7z",
    "application/x-tar": "tar",
    "application/gzip": "gz",
    "application/json": "json",
    "application/xml": "xml",
    "text/html": "html",
    "text/css": "css",
    "application/javascript": "js",
    "application/typescript": "ts",
  };

  return mimeMap[mimeType] || null;
};

const getFileIcon = (extension: string): React.ReactElement => {
  const ext = extension.includes("/")
    ? getExtensionFromMimeType(extension) // MIME type
    : extension.toLowerCase(); // Estensione

  // Restituisce l'icona corrispondente o una generica se non trovata
  return ext ? iconMap[ext] : <InsertDriveFile color="disabled" />;
};

const preferDarkMode = () => {
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
};

const getUserText = (user: User) => {
  if (user.name && user.surname) {
    return `${user.name.trim()} ${user.surname.trim()}`;
  } else if (user.email) {
    return user.email.trim();
  } else {
    return user.username.trim();
  }
};

function toPascalCase(string: string): string {
  return `${string}`
    .replace(new RegExp(/[-_]+/, "g"), " ")
    .replace(new RegExp(/[^\w\s]/, "g"), "")
    .replace(
      new RegExp(/\s+(.)(\w*)/, "g"),
      ($1, $2, $3) => `${$2.toUpperCase() + $3.toLowerCase()}`,
    )
    .replace(new RegExp(/\w/), s => s.toUpperCase());
}

function translate(key: string, fallback?: string): string {
  const i18nState = store.getState().i18n;
  const lang = i18nState.lang;
  const translations = i18nState.translations;
  if (!translations) {
    return key;
  }
  const translation = get(translations[lang], key) as string | undefined;
  return translation ?? fallback ?? key;
}

const getProgressColor = (val: number, theme: AppTheme): string => {
  return val < 100 ? theme.colors.orange : theme.colors.green;
};

const download = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url); // Libera memoria
};

const getAssetIds = (assets: Asset[]): string[] => {
  return assets.flatMap(asset => {
    const childIds = asset.children?.length ? getAssetIds(asset.children) : [];
    return [asset.id, ...childIds];
  });
};

const findAsset = (roots: Asset[], assetId: string): Asset | undefined => {
  let found: Asset | undefined = undefined;

  const traverse = (node: Asset, assetId: string): void => {
    if (node.id === assetId) {
      found = node;
      return;
    }
    // ricorsione sui figli
    node.children.forEach(c => traverse(c, assetId));
  };

  // avvia la traversata da ogni radice
  roots.forEach(c => traverse(c, assetId));

  if (!found && roots.length > 0) {
    console.warn(`Asset with id ${assetId} not found`);
  }

  return found;
};

// Mantiene gli asset il cui nome contiene il testo (con l'intero sottoalbero)
// e gli antenati necessari a raggiungerli
const filterAssets = (assets: Asset[], text: string): Asset[] => {
  const query = text.trim().toLowerCase();
  if (!query) return assets;

  return assets.flatMap(asset => {
    if (asset.name.toLowerCase().includes(query)) return [asset];
    const children = filterAssets(asset.children ?? [], query);
    return children.length ? [{...asset, children}] : [];
  });
};

const findAssetByType = (
  roots: Asset[],
  type: AssetType,
): Asset[] | undefined => {
  const found: Asset[] = [];

  const traverse = (node: Asset, type: AssetType): void => {
    if (node.type === type) {
      found.push(node);
      return;
    }
    // ricorsione sui figli
    node.children.forEach(c => traverse(c, type));
  };

  // avvia la traversata da ogni radice
  roots.forEach(c => traverse(c, type));

  return found;
};

const renderAssetTypeIcon = (
  type: AssetType | undefined,
  size?: "inherit" | "large" | "medium" | "small",
  color?: SvgIconOwnProps["color"],
) => {
  switch (type) {
    case "BUILDING":
      return (
        <AssetBuildingIcon
          color={color ?? "action"}
          fontSize={size ?? "inherit"}
        />
      );
    case "PRODUCTION":
      return (
        <AssetProductionIcon
          color={color ?? "action"}
          fontSize={size ?? "inherit"}
        />
      );
    case "PRODUCT":
      return (
        <AssetProductIcon
          color={color ?? "action"}
          fontSize={size ?? "inherit"}
        />
      );
    case "MACHINE":
      return (
        <AssetMachineIcon
          color={color ?? "action"}
          fontSize={size ?? "inherit"}
        />
      );
    case "VEHICLE":
      return (
        <AssetVehicleIcon
          color={color ?? "action"}
          fontSize={size ?? "inherit"}
        />
      );
    case "SENSOR_SCALAR":
      return (
        <SensorScalarIcon
          color={color ?? "action"}
          fontSize={size ?? "inherit"}
        />
      );
    case "SENSOR_MULTIVALUE":
      return (
        <SensorMultivalueIcon
          color={color ?? "action"}
          fontSize={size ?? "inherit"}
        />
      );
    default:
      return (
        <AssetIcon color={color ?? "action"} fontSize={size ?? "inherit"} />
      );
  }
};

const truncateSx: SxProps<Theme> = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const truncateMultilineSx: SxProps<Theme> = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
};

// colonne di un sensore multivalore, da stringa separata da virgole o array
const parseSensorColumns = (columns: unknown): string[] => {
  const list =
    typeof columns === "string"
      ? columns.split(",")
      : Array.isArray(columns)
        ? columns
        : [];
  return list.map(c => String(c).trim()).filter(c => c !== "");
};

const isAdmin = (role: Role) => {
  return role === "ADMIN" || role === "TENANT_ADMIN";
};

/**
 * Admin e project manager: creano ticket e task e gestiscono le produzioni.
 */
const isManager = (role?: Role) => {
  return !!role && (isAdmin(role) || role === "PM");
};

/**
 * Gestione e consultazione delle produzioni: admin e project manager.
 * Gli altri ruoli vedono solo i task assegnati.
 */
const canManageArtifacts = (role?: Role) => isManager(role);

/**
 * Modifica e cancellazione di ticket e task: gli admin li gestiscono tutti, il project manager
 * quelli creati da lui o a lui assegnati, come nel backend.
 */
const canManageOwned = (
  user: {id: string; role: Role} | undefined,
  entity: {userId?: string; assigneeId?: string},
) => {
  if (!user) return false;
  if (isAdmin(user.role)) return true;
  return (
    user.role === "PM" &&
    (entity.userId === user.id || entity.assigneeId === user.id)
  );
};

const getTicketPriorityColor = (theme: AppTheme, priority: ticketPriority) => {
  switch (priority) {
    case 2:
      return theme.colors.red;
    case 1:
      return theme.colors.yellow;
    case 0:
      return theme.colors.blue;
  }
};

export {
  toPascalCase,
  translate,
  preferDarkMode,
  generateColorPalette,
  acronym,
  getColor,
  getUserAvatarColor,
  hasPermission,
  getFileIcon,
  getUserText,
  getProgressColor,
  download,
  findAsset,
  findAssetByType,
  filterAssets,
  getAssetIds,
  renderAssetTypeIcon,
  isAdmin,
  isManager,
  canManageArtifacts,
  canManageOwned,
  parseSensorColumns,
  getTicketPriorityColor,
  truncateSx,
  truncateMultilineSx,
};
